import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';

export type ToastTone = 'success' | 'error' | 'info';
export interface ToastMessage {
  id: number;
  tone: ToastTone;
  text: string;
}

/** Single transient message; a new one replaces the previous. */
export function useToast(timeout = 6000) {
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const dismiss = useCallback(() => setToast(null), []);
  const showToast = useCallback((text: string, tone: ToastTone = 'info') => {
    setToast({ id: Date.now(), tone, text });
  }, []);
  useEffect(() => {
    if (!toast) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), timeout);
    return () => clearTimeout(timer.current);
  }, [toast, timeout]);
  return { toast, showToast, dismiss };
}

const TONES: Record<ToastTone, string> = {
  success: 'border-green-200 bg-green-50 text-green-900',
  error: 'border-red-200 bg-red-50 text-red-900',
  info: 'border-slate-200 bg-white text-slate-800',
};

/** Live region is always mounted so screen readers announce new messages. */
export default function Toast({ toast, onDismiss }: { toast: ToastMessage | null; onDismiss: () => void }) {
  const Icon = toast?.tone === 'success' ? CheckCircle : toast?.tone === 'error' ? AlertCircle : Info;
  return (
    <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex justify-center sm:inset-x-auto sm:right-6">
      {toast && (
        <div key={toast.id} className={`pointer-events-auto flex max-w-md items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ${TONES[toast.tone]}`}>
          <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p className="min-w-0 flex-1">{toast.text}</p>
          <button type="button" onClick={onDismiss} aria-label="Cerrar aviso" className="-m-1 rounded p-1 opacity-70 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-blue-500">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
